import React, { FC, useEffect, useId, useRef } from 'react';
import { CoinPile } from './coin-pile';
import {
  describeCountdown,
  formatCoins,
  formatCountdown,
  formatUnlockTime,
} from './coin-wallet';
import {
  ClaimState,
  useCoinWallet,
  usePrefersReducedMotion,
} from './use-coin-wallet';
import './coin-wallet.css';

export type DailyDealView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'claiming' }
  | {
      readonly kind: 'success';
      readonly claimed: number;
      readonly balance: number;
      readonly remainingMs: number | null;
    }
  | {
      readonly kind: 'cooldown';
      readonly remainingMs: number;
      readonly nextRewardAt: number;
      readonly notice?: string;
    };

export interface DailyDealCardProps {
  readonly view: DailyDealView;
  /** Server-provided amount; only shown, never sent. */
  readonly amount: number | null;
  readonly now: number;
  readonly claimError?: string | null;
  readonly onClaim: () => void;
  readonly onRetry?: () => void;
  readonly compact?: boolean;
  readonly reducedMotion?: boolean;
}

const BURST = [8, 22, 37, 51, 64, 78, 90];

/** Presentational: every state is explicit and nothing claims success before the server does. */
export const DailyDealCard: FC<DailyDealCardProps> = ({
  view,
  amount,
  now,
  claimError,
  onClaim,
  onRetry,
  compact = false,
  reducedMotion = false,
}) => {
  const titleId = useId();
  const amountLabel = amount !== null ? formatCoins(amount) : null;
  return (
    <section
      className={`cw-deal ${compact ? 'cw-deal--compact' : ''}`}
      aria-labelledby={titleId}
      aria-busy={view.kind === 'loading' || view.kind === 'claiming'}
      data-state={view.kind}
    >
      {view.kind === 'success' && !reducedMotion && (
        <span className="cw-burst" aria-hidden="true">
          {BURST.map((left, i) => (
            <span
              key={left}
              className="cw-burst__coin"
              style={{ left: `${left}%`, animationDelay: `${i * 70}ms` }}
            />
          ))}
        </span>
      )}

      <div className="cw-deal__art">
        <CoinPile
          size={compact ? 'lg' : 'xl'}
          animated={!reducedMotion && view.kind === 'ready'}
        />
      </div>

      <div>
        <p className="cw-deal__eyebrow">Daily deal</p>
        <h2 className="cw-deal__title" id={titleId}>
          {amountLabel ? (
            <>
              {amountLabel}
              <small>coins</small>
            </>
          ) : (
            'Free coins'
          )}
        </h2>
        <p className="cw-deal__body">
          {amountLabel
            ? `The same ${amountLabel} coins every 12 hours.`
            : 'Free coins every 12 hours.'}{' '}
          No spins, no ads — just tap to add them to your wallet.
        </p>
      </div>

      <div className="cw-deal__footer">
        {view.kind === 'loading' && (
          <>
            <span className="cw-deal__skeleton" />
            <span className="st-sr-only">Checking your daily deal…</span>
          </>
        )}

        {view.kind === 'error' && (
          <>
            <p className="cw-deal__error" role="alert">
              We couldn&apos;t check your daily deal right now.
            </p>
            {onRetry && (
              <button
                type="button"
                className="st-btn st-btn--on-ivory st-btn--block"
                onClick={onRetry}
              >
                Try again
              </button>
            )}
          </>
        )}

        {(view.kind === 'ready' || view.kind === 'claiming') && (
          <>
            {claimError && (
              <p className="cw-deal__error" role="alert">
                {claimError}
              </p>
            )}
            <button
              type="button"
              className="st-btn st-btn--primary st-btn--block"
              onClick={onClaim}
              disabled={view.kind === 'claiming'}
            >
              {view.kind === 'claiming'
                ? 'Adding to your wallet…'
                : amountLabel
                  ? `Add ${amountLabel} coins`
                  : 'Claim daily deal'}
            </button>
          </>
        )}

        {view.kind === 'success' && (
          <>
            <p className="cw-deal__success" role="status">
              <span aria-hidden="true">✓</span>+{formatCoins(view.claimed)}{' '}
              coins added · balance {formatCoins(view.balance)}
            </p>
            {view.remainingMs !== null && (
              <p className="cw-deal__status">
                <span>Next deal in</span>
                <span className="cw-deal__clock">
                  {formatCountdown(view.remainingMs)}
                </span>
              </p>
            )}
          </>
        )}

        {view.kind === 'cooldown' && (
          <>
            {view.notice && (
              <p className="cw-deal__error" role="status">
                {view.notice}
              </p>
            )}
            <p className="cw-deal__status">
              <span>Next deal {formatUnlockTime(view.nextRewardAt, now)}</span>
              <span
                className="cw-deal__clock"
                aria-label={`in ${describeCountdown(view.remainingMs)}`}
              >
                {formatCountdown(view.remainingMs)}
              </span>
            </p>
            <button
              type="button"
              className="st-btn st-btn--on-ivory st-btn--block"
              disabled
            >
              Already in your wallet today
            </button>
          </>
        )}
      </div>
    </section>
  );
};

/** Maps hook state to the card's view. Exported for tests. */
export const deriveDealView = (args: {
  enabled: boolean;
  isLoading: boolean;
  isError: boolean;
  hasWallet: boolean;
  status: ReturnType<typeof useCoinWallet>['status'];
  claim: ClaimState;
}): DailyDealView => {
  const { isLoading, isError, hasWallet, status, claim } = args;
  if (claim.kind === 'claiming') return { kind: 'claiming' };
  if (claim.kind === 'success') {
    return {
      kind: 'success',
      claimed: claim.claimed,
      balance: claim.balance,
      remainingMs: status?.kind === 'cooldown' ? status.remainingMs : null,
    };
  }
  if (!hasWallet) {
    if (isError) return { kind: 'error' };
    return { kind: 'loading' };
  }
  if (isLoading && !status) return { kind: 'loading' };
  if (status?.kind === 'cooldown') {
    return {
      kind: 'cooldown',
      remainingMs: status.remainingMs,
      nextRewardAt: status.nextRewardAt,
      notice:
        claim.kind === 'error' && claim.cooldown ? claim.message : undefined,
    };
  }
  return { kind: 'ready' };
};

export interface DailyDealProps {
  readonly compact?: boolean;
  /** Fired when a claim request starts. */
  readonly onClaimStart?: () => void;
  /** Fired only after the server confirmed the credit, with the server amount. */
  readonly onClaimed?: (claimed: number, balance: number) => void;
  /** Fired when the success state shows/hides. */
  readonly onSuccessChange?: (showing: boolean) => void;
}

/** Connected daily deal: wallet query + server claim. */
export const DailyDeal: FC<DailyDealProps> = ({
  compact,
  onClaimStart,
  onClaimed,
  onSuccessChange,
}) => {
  const w = useCoinWallet();
  const reducedMotion = usePrefersReducedMotion();
  const view = deriveDealView({
    enabled: w.enabled,
    isLoading: w.isLoading,
    isError: w.isError,
    hasWallet: !!w.wallet,
    status: w.status,
    claim: w.claim,
  });

  const reported = useRef(false);
  useEffect(() => {
    const success = w.claim.kind === 'success';
    onSuccessChange?.(success);
    if (w.claim.kind === 'success' && !reported.current) {
      reported.current = true;
      onClaimed?.(w.claim.claimed, w.claim.balance);
    }
    if (!success) reported.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w.claim]);

  if (!w.enabled) return null;

  return (
    <DailyDealCard
      view={view}
      amount={w.wallet?.rewardAmount ?? null}
      now={w.now}
      compact={compact}
      reducedMotion={reducedMotion}
      claimError={
        w.claim.kind === 'error' && !w.claim.cooldown ? w.claim.message : null
      }
      onClaim={() => {
        onClaimStart?.();
        w.claimReward();
      }}
      onRetry={w.refetch}
    />
  );
};
